/**
 * The printed page's reads, declared ONCE (PRD-625, docs/FRONTEND.md law 3): the flip (the page, its image

 * and the arrows) and the settle for a dragged rectangle (shown before OK, nothing written). The image read
 * (a refusal's words reach the screen: an `img` cannot read a 404 or 503 body) lands after PRD-626's typed
 * `page-image` route is in openapi.json, as a call on the generated client. Three
 * screens show the page and none may import another, so the reads live here. They are keyed by the SHEET
 * alone: a flip invalidates nothing about the rule, and the rule's reads never touch these.
 */
import { useQuery } from '@tanstack/react-query';
import { adminClient } from './admin';
import { dataOrThrow } from './typed';
import type { components } from './types/api';

export type BookName = components['schemas']['BookName'];
export type PageFlip = components['schemas']['PageFlip'];
export type BoxSettled = components['schemas']['BoxSettled'];

/** The rectangle a person dragged, in PDF points. */
export interface DraggedBox {
  readonly left: number;
  readonly bottom: number;
  readonly right: number;
  readonly top: number;
}

/** One sheet of one book: the flip's and the image's whole key. */
export interface Sheet {
  readonly book: BookName;
  readonly page: number;
}

/** What a screen asks the settle: nothing yet, or where a dragged rectangle lands. */
export type SettleAsk = { readonly kind: 'idle' } | { readonly kind: 'dragged'; readonly box: DraggedBox };

export const flipKey = (sheet: Sheet) => ['review', 'page', sheet.book, sheet.page] as const;
export const settleKey = (sheet: Sheet, box: DraggedBox) =>
  ['review', 'settle', sheet.book, sheet.page, box.left, box.bottom, box.right, box.top] as const;

/** The flip: the printed page, its image address and the sheets beside it. Reads no rule state. */
export async function readFlip(sheet: Sheet): Promise<PageFlip> {
  const response = await adminClient.GET('/api/review/page', {
    params: { query: { code: sheet.book, number: sheet.page } },
  });
  return dataOrThrow(response, 'the page');
}

/** Where the flex will put a dragged rectangle: asked before OK, and nothing is written. */
export async function readSettle(sheet: Sheet, box: DraggedBox): Promise<BoxSettled> {
  const response = await adminClient.GET('/api/review/settle', {
    params: { query: { book: sheet.book, page: sheet.page, ...box } },
  });
  return dataOrThrow(response, 'the settled box');
}

/**
 * The flip's image without the gutter numerals drawn into it: the address the flip names, with its gutter asked
 * plain. A screen that numbers the lines itself in the page (a numeral per band, selectable and read by assistive
 * technology) shows this one, so the two sets of numerals never lie over each other.
 */
export function plainImage(numbered: string): string {
  const address = new URL(numbered, 'http://door.invalid');
  address.searchParams.set('lines', 'plain');
  return `${address.pathname}${address.search}`;
}

export function usePageFlip(sheet: Sheet) {
  return useQuery({ queryKey: flipKey(sheet), queryFn: () => readFlip(sheet) });
}

const idleKey = ['review', 'settle', 'idle'] as const;

/** The settle for what was asked: an idle ask has no box, so it is never sent. */
function settleOf(sheet: Sheet, ask: SettleAsk): Promise<BoxSettled> {
  switch (ask.kind) {
    case 'idle':
      return Promise.reject(new Error('no box to settle'));
    case 'dragged':
      return readSettle(sheet, ask.box);
  }
}

/** Asked only once there is a dragged box to settle. */
export function useSettle(sheet: Sheet, ask: SettleAsk) {
  return useQuery({
    queryKey: ask.kind === 'dragged' ? settleKey(sheet, ask.box) : idleKey,
    queryFn: () => settleOf(sheet, ask),
    enabled: ask.kind === 'dragged',
  });
}
